import type { AnalyticsObject, AnalyticsProperties } from '@core/analytics/types'
import { mapProvider, type ProviderProperties } from '@core/analytics/objects/Provider'
import { type Competitiveness, getCompetitiveness } from '../competitiveness'
import type { GroupedQuotes, Tab } from '../../types'
import { getBestTransferTime } from '../transfer-time'
import { exchangeRateMargin, totalCost, totalStandardCost } from '../../helpers/pricing'

export type ComparisonResultData = {
  result: GroupedQuotes
  tab: Tab
  language: string
  comparisonId: string
  ctaLocation?: string
  amount: number
  amountUSD: number
  midMarketRate: number
}

export class ComparisonResult implements AnalyticsObject {
  name: string
  properties: ComparisonResultProperties
  constructor(properties: ComparisonResultProperties) {
    this.name = 'IMT Comparison Result'
    this.properties = properties
  }

  static getInstance({
    result,
    tab,
    language,
    comparisonId,
    ctaLocation,
    ...data
  }: ComparisonResultData) {
    const quote = result.cheapestService.quote
    return new ComparisonResult({
      comparisonResult: {
        comparisonId,
        ctaLocation,
        ranking: getRanking(result, tab),
        rankingStandardPricing: getStandardPriceRanking(result, tab),
        exposureIndexRank: getExposureRanking(result, tab),
        competitiveness: getCompetitiveness(
          totalCost(data.amount, data.midMarketRate, quote),
          data.amountUSD,
        ),
        isSpotlight: Boolean(result.label),
        hasPromotion: result.services.some((s) => Boolean(s.promotion)),
        totalCost: totalCost(data.amount, data.midMarketRate, quote),
        totalCostStandardPricing: totalStandardCost(data.amount, data.midMarketRate, quote),
        ratioFeesErm: exchangeRateMargin(quote.rate, data.midMarketRate),
        transferTime: getBestTransferTime(result),
        allPaymentOptions: result.services.flatMap((s) => s.payins.map((p) => SENDING_MAP[p])),
        languageNotMatching: !result.psp.languages.some((l) => l.code === language),
        exposureIndex: result.exposure ?? undefined,
        hasValuePropositions: Boolean(result.valueProps && result.valueProps.length > 0),
        hasInfoMessage: Boolean(
          result.customMessages.information && result.customMessages.information.length > 0,
        ),
        hasCautionMessage: Boolean(
          result.customMessages.caution && result.customMessages.caution.length > 0,
        ),
        hasWarningMessage: Boolean(
          result.customMessages.warning && result.customMessages.warning.length > 0,
        ),
        payoutNetworks: result.psp.payoutNetworks
          .filter((pon) =>
            pon.payouts.some((po) => result.services.some((s) => s.quote.payout === po)),
          )
          .map((n) => n.slug),
      },
      ...mapProvider({
        label: result.psp.name,
        slug: result.psp.slug,
        affiliate: result.psp.affiliate,
        languages: result.psp.languages.map((l) => l.code),
        reviews: { monitoScore: result.psp.score?.value ?? 0 },
        category: { type: result.psp.type },
      }),
    })
  }
}

export interface ComparisonResultProperties extends AnalyticsProperties, ProviderProperties {
  comparisonResult: {
    comparisonId: string
    ranking: number | undefined
    rankingStandardPricing: number | undefined
    exposureIndexRank: number | undefined
    competitiveness: Competitiveness
    isSpotlight: boolean
    hasPromotion: boolean
    totalCost: number
    totalCostStandardPricing: number
    ratioFeesErm: number
    transferTime: { id: string; min: number; max: number } | undefined
    allPaymentOptions: string[]
    languageNotMatching: boolean
    ctaLocation: string | undefined
    exposureIndex: number | undefined
    hasValuePropositions: boolean
    hasInfoMessage: boolean
    hasCautionMessage: boolean
    hasWarningMessage: boolean
    payoutNetworks: string[]
  }
}

export const SENDING_MAP = {
  CREDITCARD: 'Credit Card',
  DEBITCARD: 'Debit Card',
  BANK: 'Bank Transfer',
  INTERNATIONAL_BANK: 'International Bank Transfer',
  DIRECT: 'Direct Debit',
  WALLET: 'Mobile Wallet',
  TOPUP: 'Airtime Topup',
  CASH: 'Cash',
  PAYPAL: 'Paypay',
  BILL: 'Bill',
  CARD: 'Prepaid Card',
  DELIVERY: 'Cash Home Delivery',
} as const

function getRanking(result: GroupedQuotes, tab: Tab) {
  const index = tab.items.findIndex((item) => item.psp.slug === result.psp.slug)
  return index === -1 ? undefined : index + 1
}

function getStandardPriceRanking(result: GroupedQuotes, tab: Tab) {
  const sorted = [...tab.items].sort((a, b) => getStandardAmount(a) - getStandardAmount(b))
  const index = sorted.findIndex((item) => item.psp.slug === result.psp.slug)
  return index === -1 ? undefined : index + 1
}

export function getExposureRanking(result: GroupedQuotes, tab: Tab) {
  const index = [...new Set(tab.items.map((item) => item.exposure ?? 0))]
    .sort((a, b) => (b < a ? -1 : 1))
    .indexOf(result.exposure ?? 0)
  return index === -1 ? undefined : index + 1
}

export function getStandardAmount(provider: GroupedQuotes) {
  let bestAmount = 0
  for (const { quote } of provider.services) {
    if (quote.receivedAmount > bestAmount) {
      bestAmount = quote.receivedAmount
    }
  }
  return bestAmount
}
